import { useEffect, useRef, useState } from "react";
import { formatApiError } from "./api";

// A return URL is a reference to verify, never proof of payment.
export function useCheckoutReturn({ sessionId, request, isComplete, isTerminal, getBlock, onComplete, maxAttempts = 8, interval = 2500 }) {
  const callbacks = useRef();
  callbacks.current = { request, isComplete, isTerminal, getBlock, onComplete };
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ key: null, status: "idle", message: "", data: null });

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    let timer;
    let count = 0;
    setResult({ key: sessionId, status: "checking", message: "Verifica del pagamento in corso…", data: null });
    const tick = async () => {
      try {
        const data = await callbacks.current.request();
        if (!active) return;
        const block = callbacks.current.getBlock?.(data);
        if (block) {
          setResult({ key: sessionId, ...block, data });
          return;
        }
        if (callbacks.current.isComplete(data)) {
          setResult({ key: sessionId, status: "complete", message: "", data });
          callbacks.current.onComplete?.(data);
          return;
        }
        if (callbacks.current.isTerminal?.(data)) {
          setResult({ key: sessionId, status: "closed", message: "Il pagamento non risulta completato. La sessione è terminata o annullata.", data });
          return;
        }
        count += 1;
        if (count >= maxAttempts) {
          setResult({ key: sessionId, status: "pending", message: "La conferma del pagamento non è ancora disponibile. Verifica di nuovo prima di effettuare un altro pagamento.", data });
          return;
        }
        timer = setTimeout(tick, interval);
      } catch (error) {
        if (!active) return;
        const detail = error.response?.data?.detail;
        setResult({ key: sessionId, status: "error", message: detail ? formatApiError(detail) : "Verifica non riuscita. Controlla la connessione e riprova prima di effettuare un altro pagamento.", data: null });
      }
    };
    tick();
    return () => { active = false; clearTimeout(timer); };
  }, [sessionId, attempt, maxAttempts, interval]);

  const current = sessionId
    ? result.key === sessionId ? result : { status: "checking", message: "Verifica del pagamento in corso…", data: null }
    : { status: "idle", message: "", data: null };
  return { ...current, unresolved: ["checking", "pending", "error", "review"].includes(current.status), retry: () => setAttempt(value => value + 1) };
}
